export interface TermsClause {
  id: string;
  text: string;
  isDefault?: boolean;
}

export interface TermsCategory {
  id: string;
  name: string;
  clauses: TermsClause[];
}

export const TERMS_CATEGORIES: TermsCategory[] = [
  {
    id: "payment",
    name: "Payment Terms",
    clauses: [
      { id: "payment_full", text: "Full payment due upon acceptance of quote" },
      { id: "payment_50_30", text: "50% deposit required to confirm booking, balance due 30 days before travel" },
      { id: "payment_30_60", text: "30% payment to confirm, the balance 60 days ahead of arrival" },
      { id: "payment_20_60", text: "20% payment to confirm, the balance 60 days ahead of arrival" },
      { id: "payment_20_30", text: "20% payment to confirm, the balance 30 days ahead of arrival" },
      { id: "payment_60_days", text: "Payment in full required 60 days prior to departure" },
      { id: "payment_non_refundable", text: "All payments are non-refundable once travel has commenced" },
    ]
  },
  {
    id: "currency",
    name: "Currency & Pricing",
    clauses: [
      { id: "usd_from_zar", text: "USD rates are converted from ZAR base rates" },
      { id: "usd_invoice", text: "Upon acceptance of quote, a USD invoice will be issued" },
      { id: "flight_fluctuation", text: "Due to the fluctuation of flight prices and availability, upon acceptance of the quote, the programme rate is adjusted should prices have increased" },
      { id: "prices_subject_change", text: "Prices are subject to change until booking is confirmed" },
      { id: "quote_validity", text: "Quoted rates are valid for 14 days from date of quote" },
    ]
  },
  {
    id: "cancellation",
    name: "Cancellation Policy",
    clauses: [
      { id: "cancel_60_plus", text: "Cancellations 60+ days before departure: Full refund less 10% admin fee" },
      { id: "cancel_30_59", text: "Cancellations 30-59 days before departure: 50% refund" },
      { id: "cancel_0_29", text: "Cancellations 0-29 days before departure: No refund" },
      { id: "cancel_90_plus", text: "90+ days before departure: 50% cancellation fee" },
      { id: "cancel_30_90", text: "30-90 days before departure: 75% cancellation fee" },
      { id: "cancel_29_less", text: "29 days or less before departure: 100% cancellation fee" },
      { id: "no_show", text: "No-shows are non-refundable" },
    ]
  },
  {
    id: "amendments",
    name: "Amendments",
    clauses: [
      { id: "amendment_14_days", text: "Amendment requests must be made in writing at least 14 days before travel" },
      { id: "amendment_fees", text: "Amendment fees may apply based on supplier policies" },
      { id: "date_changes", text: "Date changes are subject to availability and may incur additional costs" },
    ]
  },
  {
    id: "liability",
    name: "Liability & Insurance",
    clauses: [
      { id: "insurance_recommended", text: "Travel insurance is highly recommended and is the responsibility of the traveller", isDefault: true },
      { id: "third_party_liability", text: "We are not liable for delays, cancellations, or changes caused by third-party providers" },
      { id: "force_majeure", text: "Force majeure events are excluded from our liability" },
    ]
  },
  {
    id: "travel_requirements",
    name: "Travel Requirements",
    clauses: [
      { id: "passport_validity", text: "Valid passport required with at least 6 months validity beyond travel dates" },
      { id: "visa_responsibility", text: "Visa requirements are the responsibility of the traveller" },
    ]
  },
  {
    id: "general",
    name: "General Conditions",
    clauses: [
      { id: "gratuities", text: "Gratuities are not included unless otherwise specified" },
      { id: "weather_conditions", text: "All activities are subject to weather conditions", isDefault: true },
    ]
  },
];

// Helper to get all default clause IDs
export const getDefaultClauseIds = (): string[] => {
  return TERMS_CATEGORIES.flatMap(category => 
    category.clauses.filter(clause => clause.isDefault).map(clause => clause.id)
  );
};

// Helper to get clause text by ID
export const getClauseTextById = (id: string): string | undefined => {
  for (const category of TERMS_CATEGORIES) {
    const clause = category.clauses.find(c => c.id === id);
    if (clause) return clause.text;
  }
  return undefined;
};

// Helper to convert selected clause IDs to formatted text
export const clauseIdsToText = (selectedIds: string[]): string => {
  const texts: string[] = [];
  
  for (const category of TERMS_CATEGORIES) {
    for (const clause of category.clauses) {
      if (selectedIds.includes(clause.id)) {
        texts.push(`• ${clause.text}`);
      }
    }
  }
  
  return texts.join("\n");
};
